import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';

/**
 * A window over the page.
 *
 * The portal had none: every setting opens inline, which is right for settings.
 * The route flowchart was the first thing that needed more room than the panel
 * it sits in (14 September 2026), so this is the one place a dialog is built,
 * for whatever needs one next.
 *
 * Escape and a click on the backdrop close it. Focus moves into it and back to
 * whatever opened it, the page behind does not scroll, and assistive technology
 * is told it is a modal dialog with a name. Rendered into document.body, inside
 * `.arc-modal-root`, so printing can show the dialog and nothing else.
 */
export function Modal({ title, onClose, actions, size = 'md', children }: {
  title: ReactNode;
  onClose: () => void;
  /** Controls in the header, before the close button. Not printed. */
  actions?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  children: ReactNode;
}) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  // The latest onClose without re-running the effect below on every render:
  // that would steal focus back to the dialog each time its parent re-rendered.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, []);

  return createPortal(
    <div className="arc-modal-root">
      <div className="arc-modal-backdrop"
        onMouseDown={(e) => { if (e.target === e.currentTarget) close.current(); }}>
        <div ref={dialog} className="arc-modal" data-size={size}
          role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
          <div className="arc-modal-head">
            <h2 id={titleId} className="arc-modal-title">{title}</h2>
            <div className="arc-modal-actions">
              {actions}
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                aria-label="Close" title="Close (Esc)" onClick={() => close.current()}>
                <Icon name="close" size={15} />
              </button>
            </div>
          </div>
          <div className="arc-modal-body">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
